import React, { useEffect, useContext } from 'react'
import { IconThumbUp, IconThumbDown } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { omit } from 'lodash'
import Markdown from 'marked-react'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { formatSummaryTableContent, formatTime } from '@/utils/helpers'
import WidgetContext from '@/components/widget/widget.context'

import { AiAnswerSources } from './AiAnswerSources'
import { formatSourcesForCsv } from './AiAnswers.helpers'

/**
 * @import import { AiAnswersTable } from '@/views/AiAnswers/AiAnswersTable'
 * @param {object} props
 * @param {boolean} props.loading Whether the search log is fetching
 * @param {object[]} [props.rows] The search log rows
 * @param {object} props.dateRange The page's date range, for the CSV download
 * @param {object[]} [props.actions] Per-row actions, as AdvancedTable takes them; no Actions
 *   column when empty
 */
export const AiAnswersTable = ({ loading, rows = [], dateRange, actions = [] }) => {
  const { handleChangeCsvData } = useContext(WidgetContext)

  const columns = [
    {
      accessor: 'call_id',
      label: 'Call ID',
      isSearchable: true,
      isCopiable: true,
      collapsing: true,
      format: (id) => {
        return (
          <Link to={`/calls/${id}`} className="table-link">
            {id}
          </Link>
        )
      },
    },
    {
      accessor: 'helpful',
      label: 'Feedback',
      isSortable: true,
      searchSchema: {
        type: 'boolean',
        label: 'Feedback',
      },
      format: (value) => {
        return (
          <div className="flex-align-center small-gap">
            {value === true ? (
              <Pill success outlined icon={<IconThumbUp />} />
            ) : value === false ? (
              <Pill critical outlined icon={<IconThumbDown />} />
            ) : null}
          </div>
        )
      },
    },
    {
      accessor: 'created_at',
      label: 'Created On',
      collapsing: true,
      format: (value) => formatTime(value),
    },
    {
      accessor: 'agent_name',
      label: 'Agent Name',
      isSearchable: true,
      collapsing: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Agents',
      },
    },
    {
      accessor: 'question',
      label: 'Question',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }

        return null
      },
    },
    {
      accessor: 'answer',
      label: 'Answer',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return (
            <div className="ai-answer-markdown">
              <Markdown>{value}</Markdown>
            </div>
          )
        }

        return null
      },
    },
    {
      accessor: 'sources',
      label: 'Sources',
      // Searchable so a reviewer can pull up every answer that cited a given file.
      // AdvancedTable already flattens array cells for search, so no schema is needed.
      isSearchable: true,
      format: (sources) => <AiAnswerSources sources={sources} />,
    },
  ]

  const getHeadersForCsv = (columns) => {
    return columns.map((col) => {
      if (col.accessor === 'question' || col.accessor === 'answer' || col.accessor === 'type') {
        return omit(col, 'format')
      }

      if (col.accessor === 'sources') {
        return {
          ...col,
          format: formatSourcesForCsv,
        }
      }

      if (col.accessor === 'helpful') {
        return {
          ...col,
          format: (value) => {
            if (value === true) {
              return 'Positive'
            }
            if (value === false) {
              return 'Negative'
            }
            return 'N/A'
          },
        }
      }

      return col
    })
  }

  useEffect(() => {
    handleChangeCsvData({
      headers: getHeadersForCsv(columns),
      data: rows,
      filters: {
        startDate: format(new Date(dateRange.startDate), 'yyyy-MM-dd'),
        endDate: format(new Date(dateRange.endDate), 'yyyy-MM-dd'),
      },
      fileName: 'AI Answers',
    })
  }, [rows, dateRange])

  return (
    <Card className="no-padding" data-testid="ai-answers-page">
      <AdvancedTable
        loading={loading}
        rows={rows}
        columns={columns}
        actions={actions}
        pagination
        defaultOrderBy="created_at"
        defaultOrder="desc"
      />
    </Card>
  )
}
